import React from 'react';
import { DocPageLayout } from '../../components/DocPageLayout';
import { CodeBlock } from '../../components/CodeBlock';
import { InfoBox } from '../../components/InfoBox';
interface CheckoutSessionPageProps {
  onNavigate?: (pageId: string) => void;
}
export function CheckoutSessionPage({
  onNavigate
}: CheckoutSessionPageProps) {
  return <DocPageLayout title="Establishing a Session" data-id="element-1399">
      <div className="text-sm text-gray-500 mb-6 -mt-8" data-id="element-1400">
        <button onClick={() => onNavigate?.('overview')} className="hover:underline text-orange-600" data-id="element-1401">
          Overview
        </button>{' '}
        &gt;{' '}
        <button onClick={() => onNavigate?.('checkout')} className="hover:underline text-orange-600" data-id="element-1402">
          &lt;&lt;checkout&gt;&gt;
        </button>{' '}
        &gt; Establishing a Session
      </div>

      <p className="text-gray-700 mb-6" data-id="element-1403">
        Before you can display the hosted payment page, your server must create
        a checkout session. The session stores the order details and
        configuration for the payment interaction.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4" data-id="element-1404">
        Creating the Session
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1405">
        Send a{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1406">
          CREATE_CHECKOUT_SESSION
        </code>{' '}
        request to the gateway. You must provide the order amount, currency, and
        a return URL.
      </p>

      <CodeBlock language="JSON" title="Create Checkout Session Request" code={`{
  "apiOperation": "CREATE_CHECKOUT_SESSION",
  "order": {
    "id": "ORDER-12345",
    "amount": "100.00",
    "currency": "USD",
    "description": "Ordered goods"
  },
  "interaction": {
    "operation": "PURCHASE",
    "returnUrl": "https://your-site.com/receipt",
    "cancelUrl": "https://your-site.com/cancel",
    "merchant": {
      "name": "Your Store Name",
      "logo": "https://your-site.com/logo.png"
    }
  }
}`} data-id="element-1407" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1408">
        Session Response
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1409">
        The gateway returns a session ID and a success indicator. Store the{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1410">
          successIndicator
        </code>{' '}
        on your server to verify the payment result later.
      </p>

      <CodeBlock language="JSON" title="Session Response" code={`{
  "merchant": "TEST12345678",
  "result": "SUCCESS",
  "session": {
    "id": "SESSION000212345678901234567890",
    "updateStatus": "SUCCESS",
    "version": "abcdef1234"
  },
  "successIndicator": "abc123def456"
}`} data-id="element-1411" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1412">
        Session Parameters
      </h2>
      <div className="overflow-x-auto mb-8" data-id="element-1413">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1414">
          <thead data-id="element-1415">
            <tr className="bg-gray-50" data-id="element-1416">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1417">
                Parameter
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1418">
                Required
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1419">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-1420">
            <tr data-id="element-1421">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1422">order.id</td>
              <td className="px-4 py-3" data-id="element-1423">Optional</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1424">
                Unique identifier for the order
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1425">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1426">order.amount</td>
              <td className="px-4 py-3" data-id="element-1427">Required</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1428">
                Total amount for the order
              </td>
            </tr>
            <tr data-id="element-1429">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1430">order.currency</td>
              <td className="px-4 py-3" data-id="element-1431">Required</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1432">
                ISO 4217 currency code (e.g., USD, EUR)
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1433">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1434">
                interaction.operation
              </td>
              <td className="px-4 py-3" data-id="element-1435">Required</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1436">
                PURCHASE, AUTHORIZE, or VERIFY
              </td>
            </tr>
            <tr data-id="element-1437">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1438">
                interaction.returnUrl
              </td>
              <td className="px-4 py-3" data-id="element-1439">Required</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1440">
                URL to redirect the payer after payment
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox type="tip" title="Session Expiry" data-id="element-1441">
        Checkout sessions expire after a configurable timeout (default: 30
        minutes). Ensure the payer completes the payment within this window.
      </InfoBox>
    </DocPageLayout>;
}